import type { UploadApi, UseUploadManagerReturn } from "@wayflyer/flyui";
import { useUploadManager } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import { useAxios } from "auth-sdk";
import { useMemo, useRef } from "react";

import {
  BANK_STATEMENTS_CONFIRM_UPLOAD_API,
  BANK_STATEMENTS_CREATE_UPLOAD_API,
  BANK_STATEMENTS_DELETE_UPLOAD_API,
} from "../../../components/constants";

export interface BankStatementUpload {
  name: string;
  objectKey: string;
}

const getUploadedFileId = (uploadedFile: BankStatementUpload) => uploadedFile.objectKey;

type UseBankAccountsUploadManagerReturn = UseUploadManagerReturn<BankStatementUpload> & {
  registerFilesForGroup: (files: File[], groupUuid: string) => void;
  isContextualUpload: (objectKey: string) => boolean;
};

export const useBankAccountsUploadManager = (
  customerDomain: string,
  onUploadsSettled?: () => void
): UseBankAccountsUploadManagerReturn => {
  const client = useAxios();

  const createUploadUrl = BANK_STATEMENTS_CREATE_UPLOAD_API.replace(":customerSlug", customerDomain);
  const confirmUploadUrl = BANK_STATEMENTS_CONFIRM_UPLOAD_API.replace(":customerSlug", customerDomain);
  const deleteUploadUrl = BANK_STATEMENTS_DELETE_UPLOAD_API.replace(":customerSlug", customerDomain);

  const fileGroupUuidsRef = useRef(new WeakMap<File, string>());
  const objectKeyGroupUuidsRef = useRef(new Map<string, string>());
  const contextualObjectKeysRef = useRef(new Set<string>());

  const api = useMemo<UploadApi<BankStatementUpload>>(
    () => ({
      createPresignedUrl: async (file: File) => {
        const { data } = await client.post<FundingApplicationSchemas.UploadUrlSchema>(createUploadUrl, {
          name: file.name,
          size: file.size,
          type: file.type,
        });
        const groupUuid = fileGroupUuidsRef.current.get(file);
        if (groupUuid !== undefined) {
          objectKeyGroupUuidsRef.current.set(data.objectKey, groupUuid);
        }
        return { id: data.objectKey, urlOptions: data.urlOptions };
      },
      confirmUpload: async (id: string): Promise<BankStatementUpload> => {
        const bankAccountGroupUuid = objectKeyGroupUuidsRef.current.get(id);
        objectKeyGroupUuidsRef.current.delete(id);
        if (bankAccountGroupUuid !== undefined) {
          contextualObjectKeysRef.current.add(id);
        }
        const { data } = await client.post<BankStatementUpload>(confirmUploadUrl, {
          objectKey: id,
          triggerExtraction: true,
          ...(bankAccountGroupUuid !== undefined ? { bankAccountGroupUuid } : {}),
        });
        return data;
      },
      deleteUpload: async (uploadedFile: BankStatementUpload): Promise<void> => {
        await client.post(deleteUploadUrl, { key: uploadedFile.objectKey });
      },
    }),
    [client, createUploadUrl, confirmUploadUrl, deleteUploadUrl]
  );

  // Must be called before onFilesAdded for this batch.
  const registerFilesForGroup = (files: File[], groupUuid: string): void => {
    for (const file of files) {
      fileGroupUuidsRef.current.set(file, groupUuid);
    }
  };

  return {
    ...useUploadManager<BankStatementUpload>(api, undefined, { getUploadedFileId, onUploadsSettled }),
    registerFilesForGroup,
    isContextualUpload: (objectKey: string) => contextualObjectKeysRef.current.has(objectKey),
  };
};
